/**
 * Code examples of the developer documentation. They are written against the
 * real contract (contracts/openapi/p1plug-api.v1.yaml) and the real SDK
 * (sdk/typescript): backend code with timeouts, idempotency and signature
 * checks, not snippets that only work on a happy day.
 *
 * $P1PLUG_API_URL is the base URL you received at onboarding
 * (http://127.0.0.1:8080 against a local platform).
 */
import type { CodeSample } from '@p1plug/ui';

export const LANGUAGES = ['cURL', 'TypeScript', 'Python', 'C#'] as const;

export const firstRequest: CodeSample[] = [
  {
    language: 'cURL',
    code: `curl "$P1PLUG_API_URL/api/v1/tenant" \\
  -H "Authorization: Bearer $P1PLUG_API_KEY"`,
  },
  {
    language: 'TypeScript',
    code: `import { P1PlugClient } from "@p1plug/sdk";

const p1plug = new P1PlugClient({
  baseUrl: process.env.P1PLUG_API_URL!,
  apiKey: process.env.P1PLUG_API_KEY,
});

const tenant = await p1plug.tenant.get();
console.log(tenant.name, tenant.principal.scopes);`,
  },
  {
    language: 'Python',
    code: `import os
import requests

BASE = os.environ["P1PLUG_API_URL"]
api = requests.Session()
api.headers["Authorization"] = f"Bearer {os.environ['P1PLUG_API_KEY']}"

response = api.get(f"{BASE}/api/v1/tenant", timeout=15)
response.raise_for_status()
print(response.json()["name"])`,
  },
  {
    language: 'C#',
    code: `using System.Net.Http.Headers;
using System.Net.Http.Json;
using System.Text.Json;

var http = new HttpClient
{
    BaseAddress = new Uri(Environment.GetEnvironmentVariable("P1PLUG_API_URL")!),
    Timeout = TimeSpan.FromSeconds(15),
};
http.DefaultRequestHeaders.Authorization =
    new AuthenticationHeaderValue("Bearer", Environment.GetEnvironmentVariable("P1PLUG_API_KEY"));

var tenant = await http.GetFromJsonAsync<JsonElement>("/api/v1/tenant");
Console.WriteLine(tenant.GetProperty("name").GetString());`,
  },
];

export const firstResponse = `{
  "id": "tnt_demo_voordewind",
  "slug": "voordewind",
  "name": "Voordewind",
  "principal": { "kind": "api_key", "scopes": ["orders:read", "orders:write", "devices:read", "telemetry:read", "events:read", "webhooks:manage", "grants:read", "grants:write"] }
}`;

export const createOrder: CodeSample[] = [
  {
    language: 'cURL',
    code: `curl -X POST "$P1PLUG_API_URL/api/v1/orders" \\
  -H "Authorization: Bearer $P1PLUG_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "external_order_id": "ORD-2026-000123",
    "customer": { "external_id": "C-48213", "name": "A. de Vries", "email": "a.devries@example.nl" },
    "property": { "address": "Lindelaan 14", "postal_code": "3581 AB", "city": "Utrecht" }
  }'`,
  },
  {
    language: 'TypeScript',
    code: `// Safe to retry: the same external_order_id returns the existing order.
const { order, created } = await p1plug.orders.create({
  external_order_id: "ORD-2026-000123",
  customer: { external_id: "C-48213", name: "A. de Vries", email: "a.devries@example.nl" },
  property: { address: "Lindelaan 14", postal_code: "3581 AB", city: "Utrecht" },
});

// Keep the meter point: it is the stable handle for telemetry,
// and it survives a device replacement.
await db.customers.update("C-48213", {
  p1plugOrderId: order.id,
  p1plugMeterPointId: order.meter_point_id,
});
console.log(created ? "created" : "already existed", order.status);`,
  },
  {
    language: 'Python',
    code: `order = api.post(
    f"{BASE}/api/v1/orders",
    json={
        "external_order_id": "ORD-2026-000123",
        "customer": {"external_id": "C-48213", "name": "A. de Vries", "email": "a.devries@example.nl"},
        "property": {"address": "Lindelaan 14", "postal_code": "3581 AB", "city": "Utrecht"},
    },
    timeout=15,
)
if order.status_code == 422:
    raise ValueError(order.json()["error"]["message"])  # fix the request; a retry will not help
order.raise_for_status()

created = order.status_code == 201  # 200 means the order already existed
meter_point_id = order.json()["meter_point_id"]`,
  },
  {
    language: 'C#',
    code: `var response = await http.PostAsJsonAsync("/api/v1/orders", new
{
    external_order_id = "ORD-2026-000123",
    customer = new { external_id = "C-48213", name = "A. de Vries", email = "a.devries@example.nl" },
    property = new { address = "Lindelaan 14", postal_code = "3581 AB", city = "Utrecht" },
});
response.EnsureSuccessStatusCode();

var order = await response.Content.ReadFromJsonAsync<JsonElement>();
var created = response.StatusCode == System.Net.HttpStatusCode.Created; // 200: it already existed
var meterPointId = order.GetProperty("meter_point_id").GetString();`,
  },
];

export const orderResponse = `HTTP/1.1 201 Created

{
  "id": "ord_7wgt6ujgjt2z3px73tuq",
  "external_order_id": "ORD-2026-000123",
  "status": "awaiting_device",
  "meter_point_id": "mp_26zgqa64mzkjxsn43cca",
  "stream_id": "str_dk57rnhzkecyg7r7vqxa",
  "customer_name": "A. de Vries",
  "address": "Lindelaan 14",
  "device_id": null,
  "shipment": null,
  "created_at": "2026-10-07T19:53:20Z"
}`;

export const token: CodeSample[] = [
  {
    language: 'cURL',
    code: `curl -X POST "$P1PLUG_API_URL/oauth/token" \\
  -u "$P1PLUG_CLIENT_ID:$P1PLUG_CLIENT_SECRET" \\
  -d grant_type=client_credentials \\
  -d scope="orders:read orders:write"`,
  },
  {
    language: 'TypeScript',
    code: `// The SDK requests a token, caches it and renews it a minute before it expires.
const p1plug = new P1PlugClient({
  baseUrl: process.env.P1PLUG_API_URL!,
  oauth: {
    clientId: process.env.P1PLUG_CLIENT_ID!,
    clientSecret: process.env.P1PLUG_CLIENT_SECRET!,
    scope: "orders:read orders:write", // optional: default is every scope of the client
  },
});`,
  },
  {
    language: 'Python',
    code: `import time

_token = {"value": None, "expires_at": 0.0}

def access_token() -> str:
    """Client-credentials token, cached until a minute before it expires."""
    if _token["value"] and time.time() < _token["expires_at"] - 60:
        return _token["value"]
    response = requests.post(
        f"{BASE}/oauth/token",
        auth=(os.environ["P1PLUG_CLIENT_ID"], os.environ["P1PLUG_CLIENT_SECRET"]),
        data={"grant_type": "client_credentials", "scope": "orders:read orders:write"},
        timeout=15,
    )
    response.raise_for_status()
    body = response.json()
    _token.update(value=body["access_token"], expires_at=time.time() + body["expires_in"])
    return _token["value"]`,
  },
  {
    language: 'C#',
    code: `var credentials = Convert.ToBase64String(Encoding.UTF8.GetBytes(
    $"{Environment.GetEnvironmentVariable("P1PLUG_CLIENT_ID")}:{Environment.GetEnvironmentVariable("P1PLUG_CLIENT_SECRET")}"));

using var request = new HttpRequestMessage(HttpMethod.Post, "/oauth/token")
{
    Content = new FormUrlEncodedContent(new Dictionary<string, string>
    {
        ["grant_type"] = "client_credentials",
        ["scope"] = "orders:read orders:write",
    }),
};
request.Headers.Authorization = new AuthenticationHeaderValue("Basic", credentials);

var response = await http.SendAsync(request);
response.EnsureSuccessStatusCode();
var token = await response.Content.ReadFromJsonAsync<JsonElement>();
// Cache access_token until expires_in seconds from now, minus a minute.`,
  },
];

export const tokenResponse = `{
  "access_token": "eyJhbGciOiJFUzI1NiIsImtpZCI6Ii4uLiJ9...",
  "token_type": "Bearer",
  "expires_in": 3600,
  "scope": "orders:read orders:write"
}`;

export const registerWebhook: CodeSample[] = [
  {
    language: 'cURL',
    code: `curl -X PUT "$P1PLUG_API_URL/api/v1/webhooks/endpoint" \\
  -H "Authorization: Bearer $P1PLUG_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "url": "https://api.example.nl/p1plug/webhook",
    "secret": "'"$P1PLUG_WEBHOOK_SECRET"'",
    "event_types": ["order.status_changed", "installation.*"]
  }'`,
  },
  {
    language: 'TypeScript',
    code: `await p1plug.webhooks.configure({
  url: "https://api.example.nl/p1plug/webhook",
  secret: process.env.P1PLUG_WEBHOOK_SECRET!, // at least 16 characters; you choose it
  eventTypes: ["order.status_changed", "installation.*"], // omit to receive every event
});`,
  },
];

export const verifyWebhook: CodeSample[] = [
  {
    language: 'TypeScript',
    code: `import express from "express";
import { parseWebhookDelivery, SIGNATURE_HEADER } from "@p1plug/sdk";

const app = express();

// The signature covers the raw bytes: read the body before any JSON parsing.
app.post("/p1plug/webhook", express.raw({ type: "application/json" }), async (req, res) => {
  let events;
  try {
    events = parseWebhookDelivery(req.body, {
      secret: process.env.P1PLUG_WEBHOOK_SECRET!,
      signature: req.header(SIGNATURE_HEADER),
    });
  } catch {
    return res.status(401).end(); // not from P1Plug
  }

  // Deliveries are at-least-once: make handling idempotent on event_id.
  for (const event of events) {
    if (await db.processedEvents.exists(event.event_id)) continue;
    await handle(event);
    await db.processedEvents.insert(event.event_id);
  }
  res.status(204).end(); // any 2xx acknowledges the whole batch
});`,
  },
  {
    language: 'Python',
    code: `import hashlib
import hmac
import json
import os

from flask import Flask, abort, request

app = Flask(__name__)
SECRET = os.environ["P1PLUG_WEBHOOK_SECRET"].encode()

@app.post("/p1plug/webhook")
def p1plug_webhook():
    raw = request.get_data()  # the signature covers the raw bytes
    expected = "sha256=" + hmac.new(SECRET, raw, hashlib.sha256).hexdigest()
    if not hmac.compare_digest(expected, request.headers.get("X-P1Plug-Signature", "")):
        abort(401)

    for event in json.loads(raw):
        if already_processed(event["event_id"]):  # deliveries are at-least-once
            continue
        handle(event)
        mark_processed(event["event_id"])
    return "", 204  # any 2xx acknowledges the whole batch`,
  },
  {
    language: 'C#',
    code: `app.MapPost("/p1plug/webhook", async (HttpRequest request, IEventStore store) =>
{
    // The signature covers the raw bytes: read them before deserialising.
    using var buffer = new MemoryStream();
    await request.Body.CopyToAsync(buffer);
    var raw = buffer.ToArray();

    var secret = Encoding.UTF8.GetBytes(Environment.GetEnvironmentVariable("P1PLUG_WEBHOOK_SECRET")!);
    var expected = "sha256=" + Convert.ToHexString(HMACSHA256.HashData(secret, raw)).ToLowerInvariant();
    var given = request.Headers["X-P1Plug-Signature"].ToString();
    if (!CryptographicOperations.FixedTimeEquals(Encoding.UTF8.GetBytes(expected), Encoding.UTF8.GetBytes(given)))
        return Results.Unauthorized();

    foreach (var e in JsonSerializer.Deserialize<JsonElement[]>(raw)!)
    {
        var id = e.GetProperty("event_id").GetString()!;
        if (await store.AlreadyProcessed(id)) continue; // deliveries are at-least-once
        await Handle(e);
        await store.MarkProcessed(id);
    }
    return Results.NoContent(); // any 2xx acknowledges the whole batch
});`,
  },
];

export const webhookRequest = `POST /p1plug/webhook HTTP/1.1
Content-Type: application/json
X-P1Plug-Signature: sha256=5b1f0c…e3a9
X-P1Plug-Delivery-Id: 1913
X-P1Plug-Event-Count: 2

[
  {
    "event_id": "9b0f1c1e-5a1d-5f0e-8a53-3f1c0f6f2c11",
    "schema_version": 1,
    "type": "order.status_changed",
    "tenant_id": "tnt_demo_voordewind",
    "occurred_at": "2026-10-07T19:53:21Z",
    "cursor": "eyJzIjo0MTM4fQ.c2ln",
    "data": {
      "order_id": "ord_7wgt6ujgjt2z3px73tuq",
      "external_order_id": "ORD-2026-000123",
      "previous_status": "packed",
      "status": "shipped",
      "device_serial": "P1P-DEV-000001",
      "tracking_code": "3SP1P3PX73TUQ"
    }
  },
  {
    "event_id": "0c64a3b2-7d15-5d0a-b0de-c53a51f6a8d4",
    "schema_version": 1,
    "type": "installation.activated",
    "tenant_id": "tnt_demo_voordewind",
    "occurred_at": "2026-10-07T19:54:21Z",
    "cursor": "eyJzIjo0MTM5fQ.c2ln",
    "data": {
      "installation_id": "ins_g3outsdf5s52pc7mutqq",
      "meter_point_id": "mp_26zgqa64mzkjxsn43cca",
      "external_order_id": "ORD-2026-000123",
      "device_serial": "P1P-DEV-000001",
      "activated_at": "2026-10-07T19:54:21Z"
    }
  }
]`;

export const readEvents: CodeSample[] = [
  {
    language: 'cURL',
    code: `# First call: no cursor reads from the beginning.
curl "$P1PLUG_API_URL/api/v1/events?limit=500" \\
  -H "Authorization: Bearer $P1PLUG_API_KEY"

# Then continue from the next_cursor you stored.
curl "$P1PLUG_API_URL/api/v1/events?after=$CURSOR&limit=500" \\
  -H "Authorization: Bearer $P1PLUG_API_KEY"`,
  },
  {
    language: 'TypeScript',
    code: `// Store the cursor in the same transaction as your own writes:
// after a crash you resume exactly where you stopped.
let cursor = await db.settings.get("p1plug.cursor"); // undefined on the first run

for (;;) {
  const page = await p1plug.events.list({ after: cursor, limit: 500 });
  await db.transaction(async (tx) => {
    for (const event of page.events) await handle(tx, event); // idempotent on event_id
    await tx.settings.set("p1plug.cursor", page.next_cursor);
  });
  cursor = page.next_cursor;
  if (!page.has_more) await sleep(5_000);
}`,
  },
  {
    language: 'Python',
    code: `cursor = load_cursor()  # None on the first run

while True:
    params = {"limit": 500, **({"after": cursor} if cursor else {})}
    page = api.get(f"{BASE}/api/v1/events", params=params, timeout=30)
    page.raise_for_status()
    body = page.json()

    with transaction() as tx:  # your writes and the cursor commit together
        for event in body["events"]:
            handle(tx, event)  # idempotent on event_id
        save_cursor(tx, body["next_cursor"])

    cursor = body["next_cursor"]
    if not body["has_more"]:
        time.sleep(5)`,
  },
];

export const latestReading: CodeSample[] = [
  {
    language: 'cURL',
    code: `curl "$P1PLUG_API_URL/api/v1/meter-points/mp_26zgqa64mzkjxsn43cca/latest" \\
  -H "Authorization: Bearer $P1PLUG_API_KEY"`,
  },
  {
    language: 'TypeScript',
    code: `const reading = await p1plug.meterPoints.latest(meterPointId);
if (reading) {
  const netWatts = (reading.power_import_w ?? 0) - (reading.power_export_w ?? 0);
  console.log(reading.event_time, netWatts, "W");
} // null: the device has not sent a reading yet`,
  },
  {
    language: 'Python',
    code: `response = api.get(f"{BASE}/api/v1/meter-points/{meter_point_id}/latest", timeout=15)
if response.status_code == 404 and response.json()["error"]["code"] == "no_telemetry":
    reading = None  # the device has not sent a reading yet
else:
    response.raise_for_status()
    reading = response.json()`,
  },
];

export const readingResponse = `{
  "event_id": "08dd15ed-7a83-566b-b2cf-01c448811616",
  "stream_id": "str_dk57rnhzkecyg7r7vqxa",
  "installation_no": 1,
  "seq": 1585,
  "event_time": "2026-10-07T22:18:19.941Z",
  "received_at": "2026-10-07T22:18:24.060Z",
  "interval_s": 10,
  "backfill": false,
  "power_import_w": 880,
  "power_export_w": 0,
  "energy_import_t1_wh": 62193468,
  "energy_import_t2_wh": 31875992,
  "energy_export_t1_wh": 12245043,
  "energy_export_t2_wh": 27016977,
  "tariff": 1,
  "phases": [
    { "phase": 1, "voltage_dv": 2318, "current_ma": 3830 }
  ]
}`;

export const errorBody = `HTTP/1.1 422 Unprocessable Entity
Content-Type: application/json

{
  "error": {
    "code": "validation_failed",
    "message": "property.address is required"
  }
}`;

export const errorHandling: CodeSample[] = [
  {
    language: 'TypeScript',
    code: `import { P1PlugError } from "@p1plug/sdk";

try {
  await p1plug.orders.create(request);
} catch (error) {
  if (!(error instanceof P1PlugError)) throw error; // network: retry with backoff
  switch (error.code) {
    case "validation_failed":
      return reject(request, error.message); // fix the request; do not retry
    case "unauthorized":
    case "insufficient_scope":
      return alertOperations(error); // a credential problem, not this order's
    default:
      if (error.status >= 500 || error.status === 429) return retryLater(request);
      throw error;
  }
}`,
  },
  {
    language: 'Python',
    code: `response = api.post(f"{BASE}/api/v1/orders", json=order, timeout=15)
if response.ok:
    return response.json()

error = response.json().get("error", {})
if response.status_code in (429, 500, 502, 503, 504):
    raise Retryable(error.get("code"))          # same request, later, with backoff
if error.get("code") == "validation_failed":
    raise InvalidOrder(error["message"])        # fix the request; do not retry
raise IntegrationFault(response.status_code, error)  # credentials or a bug: alert`,
  },
];
